import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Sparkles, Check } from "lucide-react";
import useSEO from "@/lib/useSEO";

export default function ConciergeFeature() {
  useSEO({
    title: "Concierge UI",
    description: "An interface composed for patrons of taste. Every gesture refined, every surface considered.",
    path: "/patrons/concierge-ui",
  });
  return (
    <main data-testid="patrons-concierge" className="bg-bg text-white min-h-screen">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-24 pb-24">
        <Link to="/" data-testid="patrons-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-muted-fg hover:text-gold transition-colors">
          <ArrowLeft size={14} /> Back to the Landing
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-6">— 03 / For Patrons · Concierge UI</div>
            <h1 className="font-display text-5xl lg:text-7xl tracking-tighter font-light leading-[0.95]">
              An interface<br/><span className="italic gold-text">composed for you.</span>
            </h1>
            <p className="mt-8 text-secondary-fg text-lg leading-relaxed max-w-xl font-light">
              Obsidian surfaces, gold accents, italic serif headings, and a deliberate cadence. The y ℶα interface treats every gesture as a signature — no clutter, no advertising, no noise. The page does what you wish, when you wish it.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/register" className="btn-gold inline-flex items-center gap-2">Enter the experience <ArrowUpRight size={16} /></Link>
              <Link to="/login" className="btn-ghost">Sign in</Link>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <div className="glass p-8 rounded-md">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-fg mb-3">Design language</div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold"/> Cormorant Garamond display type</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold"/> Obsidian palette with gold accents</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold"/> Hairline borders and glass surfaces</li>
                <li className="flex items-center gap-3"><Sparkles size={16} className="text-gold"/> Considered micro-animation, never busy</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border hairline-strong">
          {[
            ["Zero clutter", "Every screen holds only what you need at that moment. Nothing competes for attention."],
            ["Italic display type", "Cormorant Garamond — the typography of a private library, not a bank app."],
            ["Hairline architecture", "Borders measured in fractions of a pixel. Surfaces float on a gold-veined obsidian."],
            ["Considered motion", "Transitions you feel, never see. Hovers reveal what you need, gently."],
            ["Always dark", "An interface built for late-night composure, not daytime hustle."],
            ["Same on every device", "Phone, tablet, laptop — the choreography is identical and quiet."],
          ].map(([t, b]) => (
            <div key={t} className="bg-bg p-8">
              <div className="flex items-start gap-3">
                <Check className="text-gold mt-1 shrink-0" size={18} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display text-xl mb-2">{t}</h3>
                  <p className="text-secondary-fg text-sm leading-relaxed font-light">{b}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 text-center">
          <h2 className="font-display text-4xl lg:text-5xl tracking-tight font-light">
            Spend with <span className="italic gold-text">discretion.</span>
          </h2>
          <div className="mt-8 flex justify-center gap-4">
            <Link to="/register" className="btn-gold inline-flex items-center gap-2">Open an account <ArrowUpRight size={16} /></Link>
          </div>
        </div>
      </section>
    </main>
  );
}
